/**
 * Annual Returns — Family transfers view.
 *
 * Read-only rollup of ledger rows whose supplier matches a household member
 * (or a joint couple supplier). Does not change classifications.
 */

import { granularDisplayCategoryName, type ReviewRowPlClassSource } from './annualPlClass';

export type FamilyTransferPersonKey = 'omar' | 'heidi' | 'dwayne' | 'lena' | 'sean' | 'coral';

export type FamilyTransferFamilyKey = 'omhe' | 'dwle' | 'seco';

export type FamilyTransferMember = {
  personKey: FamilyTransferPersonKey;
  personId: number;
  name: string;
};

export type FamilyTransferFamily = {
  familyKey: FamilyTransferFamilyKey;
  familyId: number;
  familyName: string;
  people: FamilyTransferMember[];
};

/** Canonical roster: 3 families, 6 people (matches cloud `families` / `people`). */
export const FAMILY_TRANSFER_ROSTER: FamilyTransferFamily[] = [
  {
    familyKey: 'omhe',
    familyId: 1,
    familyName: 'Omar and Heidi',
    people: [
      { personKey: 'omar', personId: 1, name: 'Omar' },
      { personKey: 'heidi', personId: 2, name: 'Heidi' },
    ],
  },
  {
    familyKey: 'dwle',
    familyId: 2,
    familyName: 'Dwayne and Lena',
    people: [
      { personKey: 'dwayne', personId: 3, name: 'Dwayne' },
      { personKey: 'lena', personId: 4, name: 'Lena' },
    ],
  },
  {
    familyKey: 'seco',
    familyId: 3,
    familyName: 'Sean and Coral',
    people: [
      { personKey: 'sean', personId: 5, name: 'Sean' },
      { personKey: 'coral', personId: 6, name: 'Coral' },
    ],
  },
];

/** Normalize supplier for matching (lowercase, strip punctuation, & → and). */
export const normalizeFamilySupplierKey = (raw: string | null | undefined): string =>
  String(raw || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’`]/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const SOLO_SUPPLIER_RE: Record<FamilyTransferPersonKey, RegExp[]> = {
  omar: [/^omar o sullivan$/],
  heidi: [/^heidi$/, /^heidi o sullivan$/],
  dwayne: [/^dwayne o sullivan$/],
  lena: [/^lena o sullivan$/],
  sean: [/^sean o sullivan$/],
  coral: [/^coral$/, /^coral o sullivan$/],
};

const JOINT_SUPPLIER_RE: Record<FamilyTransferFamilyKey, RegExp[]> = {
  omhe: [/^omar and heidi$/, /^omar and heidi o sullivan$/],
  dwle: [/^dwayne and lena$/, /^dwayne and lena o sullivan$/],
  seco: [/^sean and coral$/, /^sean and coral o sullivan$/],
};

/** Explicit non-family lookalikes (e.g. contractor Dwayne Knowles). */
const EXCLUDED_SUPPLIER_RE: RegExp[] = [
  /^dwayne knowles$/,
  /^finn o sullivan$/,
  /^naoise o sullivan$/,
];

export type FamilyTransferMatch =
  | { kind: 'person'; familyKey: FamilyTransferFamilyKey; personKey: FamilyTransferPersonKey }
  | { kind: 'joint'; familyKey: FamilyTransferFamilyKey };

export const matchFamilyTransferSupplier = (
  supplier: string | null | undefined
): FamilyTransferMatch | null => {
  const key = normalizeFamilySupplierKey(supplier);
  if (!key) return null;
  if (EXCLUDED_SUPPLIER_RE.some((re) => re.test(key))) return null;

  for (const family of FAMILY_TRANSFER_ROSTER) {
    for (const re of JOINT_SUPPLIER_RE[family.familyKey]) {
      if (re.test(key)) return { kind: 'joint', familyKey: family.familyKey };
    }
  }
  for (const family of FAMILY_TRANSFER_ROSTER) {
    for (const person of family.people) {
      for (const re of SOLO_SUPPLIER_RE[person.personKey]) {
        if (re.test(key)) {
          return { kind: 'person', familyKey: family.familyKey, personKey: person.personKey };
        }
      }
    }
  }
  return null;
};

export type FamilyTransferRowLike = ReviewRowPlClassSource & {
  amount?: number | null;
  supplier?: string | null;
  category?: string | null;
  cashFlow?: string | null;
};

export type FamilyTransferCashSide = 'in' | 'out' | 'other';

export const familyTransferCashSide = (row: FamilyTransferRowLike): FamilyTransferCashSide => {
  const s = String(row.cashFlow ?? '').trim().toLowerCase();
  if (s === 'in' || s === 'income') return 'in';
  if (s === 'out' || s === 'expense') return 'out';
  if (s === 'n/a' || s === 'na') return 'other';
  const amount = Number(row.amount);
  if (Number.isFinite(amount) && amount !== 0) return amount > 0 ? 'in' : 'out';
  return 'other';
};

export type FamilyTransferBucketTotals = {
  inAmount: number;
  outAmount: number;
  inCount: number;
  outCount: number;
  count: number;
};

const emptyTotals = (): FamilyTransferBucketTotals => ({
  inAmount: 0,
  outAmount: 0,
  inCount: 0,
  outCount: 0,
  count: 0,
});

const addRowToTotals = (
  totals: FamilyTransferBucketTotals,
  row: FamilyTransferRowLike,
  scale: number
): void => {
  const amount = Number(row.amount);
  if (!Number.isFinite(amount) || amount === 0) {
    totals.count += 1;
    return;
  }
  const scaled = amount * scale;
  const side = familyTransferCashSide(row);
  totals.count += 1;
  if (side === 'in') {
    totals.inAmount += scaled;
    totals.inCount += 1;
  } else if (side === 'out') {
    totals.outAmount += scaled;
    totals.outCount += 1;
  }
};

export type FamilyTransferPersonGroup<T> = {
  personKey: FamilyTransferPersonKey;
  personId: number;
  name: string;
  rows: T[];
  totals: FamilyTransferBucketTotals;
  /** Cash-out expense-class amounts (e.g. Employee Salary) — taxable to the person. */
  taxableIncomeAmount: number;
  taxableIncomeCount: number;
};

export type FamilyTransferFamilyGroup<T> = {
  familyKey: FamilyTransferFamilyKey;
  familyId: number;
  familyName: string;
  people: FamilyTransferPersonGroup<T>[];
  jointRows: T[];
  jointTotals: FamilyTransferBucketTotals;
  jointTaxableIncomeAmount: number;
  jointTaxableIncomeCount: number;
  rows: T[];
  totals: FamilyTransferBucketTotals;
  /** Category → signed sum (in positive, out negative) for this family. */
  categoryBreakdown: Array<{ category: string; inAmount: number; outAmount: number; count: number }>;
};

export type FamilyTransfersView<T> = {
  families: FamilyTransferFamilyGroup<T>[];
  rows: T[];
  totals: FamilyTransferBucketTotals;
};

const granularCategory = (row: FamilyTransferRowLike): string => {
  const name = granularDisplayCategoryName(row).trim();
  if (!name || name === '—' || name === '-') return '(none)';
  return name;
};

export type BuildFamilyTransfersOptions<T extends FamilyTransferRowLike = FamilyTransferRowLike> = {
  /** Multiply each row amount before summing (e.g. FX fold into display currency). */
  amountScaleForRow?: (row: T) => number;
  /**
   * P&L class for a row. Cash-out + expense counts as taxable income to that person
   * (salary etc.). Reversal / financing / owner transfers do not.
   */
  plClassForRow?: (row: T) => string;
};

/** True when cash left the biz as an expense-class payment to the person (taxable to them). */
export const isFamilyTransferTaxableIncomeRow = <T extends FamilyTransferRowLike>(
  row: T,
  plClassForRow?: (row: T) => string
): boolean => {
  if (familyTransferCashSide(row) !== 'out') return false;
  const pl = String(plClassForRow?.(row) || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]/g, '_');
  return pl === 'expense';
};

/**
 * Group filtered annual rows into the three families / six people.
 * Joint couple suppliers sit under the family, not double-counted on both people.
 */
export const buildFamilyTransfersView = <T extends FamilyTransferRowLike>(
  rows: T[],
  options?: BuildFamilyTransfersOptions<T>
): FamilyTransfersView<T> => {
  const scaleFor = options?.amountScaleForRow ?? ((_row: T) => 1);
  const plClassFor = options?.plClassForRow;

  const families: FamilyTransferFamilyGroup<T>[] = FAMILY_TRANSFER_ROSTER.map((family) => ({
    familyKey: family.familyKey,
    familyId: family.familyId,
    familyName: family.familyName,
    people: family.people.map((person) => ({
      personKey: person.personKey,
      personId: person.personId,
      name: person.name,
      rows: [] as T[],
      totals: emptyTotals(),
      taxableIncomeAmount: 0,
      taxableIncomeCount: 0,
    })),
    jointRows: [] as T[],
    jointTotals: emptyTotals(),
    jointTaxableIncomeAmount: 0,
    jointTaxableIncomeCount: 0,
    rows: [] as T[],
    totals: emptyTotals(),
    categoryBreakdown: [],
  }));

  const byKey = new Map(families.map((f) => [f.familyKey, f]));
  const allRows: T[] = [];

  const addTaxable = (
    target: { taxableIncomeAmount: number; taxableIncomeCount: number },
    row: T,
    scale: number
  ) => {
    if (!isFamilyTransferTaxableIncomeRow(row, plClassFor)) return;
    const amount = Number(row.amount);
    if (!Number.isFinite(amount) || amount === 0) return;
    target.taxableIncomeAmount += Math.abs(amount * scale);
    target.taxableIncomeCount += 1;
  };

  for (const row of rows) {
    const match = matchFamilyTransferSupplier(row.supplier);
    if (!match) continue;
    const family = byKey.get(match.familyKey);
    if (!family) continue;
    const scale = scaleFor(row);
    allRows.push(row);
    family.rows.push(row);
    addRowToTotals(family.totals, row, scale);
    if (match.kind === 'joint') {
      family.jointRows.push(row);
      addRowToTotals(family.jointTotals, row, scale);
      if (isFamilyTransferTaxableIncomeRow(row, plClassFor)) {
        const amount = Number(row.amount);
        if (Number.isFinite(amount) && amount !== 0) {
          family.jointTaxableIncomeAmount += Math.abs(amount * scale);
          family.jointTaxableIncomeCount += 1;
        }
      }
    } else {
      const person = family.people.find((p) => p.personKey === match.personKey);
      if (person) {
        person.rows.push(row);
        addRowToTotals(person.totals, row, scale);
        addTaxable(person, row, scale);
      }
    }
  }

  for (const family of families) {
    const catMap = new Map<string, { inAmount: number; outAmount: number; count: number }>();
    for (const row of family.rows) {
      const cat = granularCategory(row);
      const scale = scaleFor(row);
      const amount = Number(row.amount);
      const scaled = Number.isFinite(amount) ? amount * scale : 0;
      const side = familyTransferCashSide(row);
      let entry = catMap.get(cat);
      if (!entry) {
        entry = { inAmount: 0, outAmount: 0, count: 0 };
        catMap.set(cat, entry);
      }
      entry.count += 1;
      if (side === 'in') entry.inAmount += scaled;
      else if (side === 'out') entry.outAmount += scaled;
    }
    family.categoryBreakdown = Array.from(catMap.entries())
      .map(([category, v]) => ({ category, ...v }))
      .sort(
        (a, b) =>
          Math.abs(b.inAmount) +
          Math.abs(b.outAmount) -
          (Math.abs(a.inAmount) + Math.abs(a.outAmount)) || a.category.localeCompare(b.category)
      );
  }

  const totals = emptyTotals();
  for (const row of allRows) {
    addRowToTotals(totals, row, scaleFor(row));
  }

  return { families, rows: allRows, totals };
};

export const FAMILY_TRANSFERS_SECTION_PREFIX = 'family_transfers:';

export const familyTransferAccordionKey = (familyKey: FamilyTransferFamilyKey): string =>
  `${FAMILY_TRANSFERS_SECTION_PREFIX}${familyKey}`;

export type FamilyTransferChartRow = {
  label: string;
  familyKey: FamilyTransferFamilyKey;
  In: number;
  Out: number;
  inCount: number;
  outCount: number;
};

const roundChartMoney = (n: number): number => Math.round((Number(n) || 0) * 100) / 100;

/** One column per family: In above zero, Out below zero (same signed chart convention). */
export const buildFamilyTransferChartRows = <T,>(
  view: FamilyTransfersView<T>
): FamilyTransferChartRow[] =>
  view.families.map((family) => ({
    label: family.familyName,
    familyKey: family.familyKey,
    In: roundChartMoney(family.totals.inAmount),
    Out: roundChartMoney(-Math.abs(family.totals.outAmount)),
    inCount: family.totals.inCount,
    outCount: family.totals.outCount,
  }));

